import React, { useState } from "react";

import { faDownload } from "@fortawesome/pro-regular-svg-icons";

import { Checkbox } from "src/base-components/Checkbox";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Button } from "src/design-system/Button";
import { Callout } from "src/design-system/Callout";
import { downloadPrivateKeyPem } from "src/oauthClients/keyMaterial";

type Props = {
  clientName: string;
  privateKeyPem: string;
  hasTakenKey: boolean;
  isSaveConfirmed: boolean;
  /** Namespaces the data-locs, so both dialogs stay addressable on their own. */
  dataLocPrefix: string;
  onKeyTaken: () => void;
  onSaveConfirmedChange: (confirmed: boolean) => void;
};

/**
 * One-time handoff of a browser-generated private key.
 *
 * The download click is no proof the file landed: a cancelled "Save as" and a
 * blocked download look identical from here, and the browser reports neither.
 * So the customer confirms the save by hand, and that confirmation, not the
 * click, is what the dialogs gate their close on.
 *
 * Copy is the second route out, and it is load-bearing rather than a
 * convenience: a browser policy that blocks downloads would otherwise leave the
 * confirmation unreachable, and with it a dialog that cannot be closed at all.
 */
export const PrivateKeyHandoff: React.FC<Props> = ({
  clientName,
  privateKeyPem,
  hasTakenKey,
  isSaveConfirmed,
  dataLocPrefix,
  onKeyTaken,
  onSaveConfirmedChange,
}) => {
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const confirmId = `${dataLocPrefix}-save-confirmed`;

  const handleDownload = () => {
    downloadPrivateKeyPem(privateKeyPem, clientName);
    setHasDownloaded(true);
    onKeyTaken();
  };

  /**
   * Deliberately not src/utils/clipboard: that helper mirrors every copied
   * string into a persistent `#clipboard-text` div for cypress, which would
   * leave the PEM on document.body for the life of the page, outliving this
   * dialog and everything it tears down. Writing straight to the clipboard also
   * lets a refused write reject, so the icon reports the failure instead of
   * showing a checkmark and arming the confirmation over nothing.
   */
  const handleCopy = async () => {
    await navigator.clipboard.writeText(privateKeyPem);
    onKeyTaken();
  };

  return (
    <div className="flex flex-col gap-4">
      <Callout
        dataLoc={`${dataLocPrefix}-private-key-warning`}
        variant="warning"
      >
        Save the private key now. It was generated in your browser, Taktile
        never received it, and this dialog is the only place it exists. Copying
        puts it on your clipboard, which may sync between your devices and
        linger in clipboard history, so prefer the file where you can.
      </Callout>
      <div className="flex items-center gap-x-3">
        <div className="flex-1">
          <Button
            dataLoc={`${dataLocPrefix}-download-private-key`}
            iconLeft={faDownload}
            variant="secondary"
            fullWidth
            onClick={handleDownload}
          >
            {hasDownloaded
              ? "Download private key again"
              : "Download private key (PEM)"}
          </Button>
        </div>
        <CopyTextIcon
          dataLoc={`${dataLocPrefix}-copy-private-key`}
          feedback="inline"
          size="sm"
          tooltip="Copy the PEM"
          onClick={handleCopy}
        />
      </div>
      <div className="flex items-center gap-x-2">
        <Checkbox
          checked={isSaveConfirmed}
          dataLoc={confirmId}
          disabled={!hasTakenKey}
          id={confirmId}
          onChange={(event) => onSaveConfirmedChange(event.target.checked)}
        />
        <label
          className="font-inter-normal-12px text-gray-600"
          htmlFor={confirmId}
        >
          I have saved the private key
        </label>
      </div>
    </div>
  );
};
